import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera, CloudUpload } from 'lucide-react';
import { Button } from '../ui';
import { validateImageFile } from '../../utils/fileSystemUtils';
import { uploadPropImages, testWasabiConnection } from '../../utils/wasabiService';
import { useToast } from './ToastContext';

const ImageUpload = ({ 
  propId, 
  existingImages = [], 
  onImagesChange,
  maxImages = 5 
}) => {
  const { toast } = useToast();
  const [images, setImages] = useState(existingImages);
  
  // Update images when existingImages prop changes (important for edit mode)
  useEffect(() => {
    setImages(existingImages);
  }, [existingImages]);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check total count
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Validate and create previews
    const newPreviews = [];
    files.forEach((file, index) => {
      try {
        validateImageFile(file);
        
        const reader = new FileReader();
        reader.onload = (event) => {
          newPreviews.push({
            id: `new_${Date.now()}_${index}`,
            file,
            url: event.target.result,
            isNew: true
          });
          
          if (newPreviews.length === files.filter(f => f).length) {
            setPreviews(prev => [...prev, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast.error(error.message);
      }
    });
    
    // Clear input
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (previews.length === 0) return;
    
    setUploading(true);
    const uploadResults = [];

    try {
      console.log('🌥️ Testing Wasabi connection...');
      const connectionOk = await testWasabiConnection();
      if (!connectionOk) {
        throw new Error('Cannot connect to Wasabi storage');
      }

      // Extract files from previews
      const filesToUpload = previews
        .filter(preview => preview.isNew)
        .map(preview => preview.file);

      if (filesToUpload.length === 0) {
        toast.warning('No new files to upload');
        return;
      }

      console.log(`☁️ Uploading ${filesToUpload.length} files to Wasabi...`);
      const results = await uploadPropImages(filesToUpload, propId);
      
      // Extract URLs from upload results
      const uploadedImageUrls = results.map(result => result.url);
      
      console.log('✅ Upload successful:', uploadedImageUrls);

      // Update images state with Wasabi URLs
      const newImages = [...images, ...uploadedImageUrls];
      setImages(newImages);
      setPreviews([]);
      
      console.log('📸 Final images array for form:', newImages);
      console.log('🔗 Calling onImagesChange with:', newImages);
      
      // Test if the uploaded URL is actually accessible
      if (uploadedImageUrls.length > 0) {
        console.log('🧪 Testing accessibility of uploaded URL:', uploadedImageUrls[0]);
        const testImg = new Image();
        testImg.onload = () => {
          console.log('✅ Wasabi URL is accessible from browser');
        };
        testImg.onerror = (error) => {
          console.error('❌ Wasabi URL is NOT accessible from browser:', error);
          console.error('❌ This means CORS or permissions issue');
        };
        testImg.src = uploadedImageUrls[0];
      }
      
      onImagesChange(newImages);
      
      toast.success(`Successfully uploaded ${uploadedImageUrls.length} image(s) to cloud storage!`);
      
      // Add reminder about saving to database
      setTimeout(() => {
        toast.warning('Remember to SAVE/UPDATE the prop to persist image URLs to database!', {
          duration: 8000
        });
      }, 2000);
      
    } catch (error) {
      console.error('☁️ Wasabi upload failed:', error);
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (imageId) => {
    // Remove from existing images
    if (images.includes(imageId)) {
      const newImages = images.filter(img => img !== imageId);
      setImages(newImages);
      onImagesChange(newImages);
      
      // Show feedback about removal
      if (imageId.startsWith('http')) {
        toast.warning('Image removed! Remember to UPDATE PROP to save changes.', {
          duration: 6000
        });
      } else {
        toast.info('Image removed from form.', {
          duration: 3000
        });
      }
    }
    
    // Remove from previews
    setPreviews(prev => prev.filter(p => p.id !== imageId));
  };

  const removePreview = (previewId) => {
    setPreviews(prev => prev.filter(p => p.id !== previewId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Images</h4>
        <span className="text-sm text-gray-500">
          {images.length + previews.length}/{maxImages}
        </span>
      </div>

      {/* Existing Images */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((image, index) => {
            // Handle both Wasabi URLs and local paths
            const imageUrl = image.startsWith('http') ? image : `/images/props/${image}`;
            return (
            <div key={image} className="relative group">
              <img
                src={imageUrl}
                alt={`Prop image ${index + 1}`}
                className="w-full h-24 object-cover rounded border"
                onError={(e) => {
                  e.target.src = '/images/fallbacks/prop-default.jpg';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded flex items-center justify-center">
                <Button
                  onClick={() => removeImage(image)}
                  variant="danger"
                  size="compact"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  title={image.startsWith('http') ? 'Remove from prop (requires save)' : 'Remove from form'}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1 rounded">
                {index === 0 ? 'Main' : index + 1}
              </div>
              {/* Show cloud icon for Wasabi URLs */}
              {image.startsWith('http') && (
                <div className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-1 rounded">
                  ☁️
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Image Previews */}
      {previews.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {previews.map((preview) => (
            <div key={preview.id} className="relative group">
              <img
                src={preview.url}
                alt="Preview"
                className="w-full h-24 object-cover rounded border border-blue-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded flex items-center justify-center">
                <Button
                  onClick={() => removePreview(preview.id)}
                  variant="danger"
                  size="compact"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-1 rounded">
                New
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Controls */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        <div className="text-center">
        <CloudUpload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 mb-2">
        Upload images to cloud storage
        </p>
        <p className="text-xs text-blue-600 mb-3">
            Images will be stored in Wasabi cloud storage
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div className="flex gap-2 justify-center">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="secondary"
              size="compact"
              icon={Upload}
              disabled={images.length + previews.length >= maxImages}
            >
              Choose Files
            </Button>
            
            {previews.length > 0 && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUpload();
                }}
                variant="primary"
                size="compact"
                disabled={uploading || !propId}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            )}
            
            {images.length > 0 && (
              <Button
                type="button"
                onClick={() => {
                  // Scroll to bottom of modal
                  const modal = document.querySelector('[role="dialog"]');
                  if (modal) {
                    modal.scrollTop = modal.scrollHeight;
                  }
                  // Flash the save button by finding it via text content
                  setTimeout(() => {
                    const buttons = Array.from(document.querySelectorAll('button'));
                    const saveBtn = buttons.find(btn => 
                      btn.textContent.includes('Update Prop') || 
                      btn.textContent.includes('Add Prop')
                    );
                    if (saveBtn) {
                      saveBtn.style.animation = 'pulse 2s infinite';
                      saveBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 500);
                }}
                variant="success"
                size="compact"
                className="animate-pulse bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                ⚠️ UPDATE PROP TO SAVE!
              </Button>
            )}
          </div>
          
          {!propId && (
            <p className="text-xs text-amber-600 mt-2">
              Save prop first to enable image upload
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Tip: First image will be used as the main display image
      </p>
    </div>
  );
};

export default ImageUpload;